import { HotelRatingSquare } from '@lt/components';
import { useTranslation } from '@lt/localization';
import React from 'react';
import styled from 'styled-components';
import { TEST_ID_HOTEL_RATING_CONTAINER } from './constants';
import { getRatingStyle } from './utils/ratingUtils';

export interface CategoryListProps {
  score: number;
  categoryName: string;
}

export interface HotelRatingProps {
  categoryList: CategoryListProps[];
  score: number;
  text: string;
  count: number;
  isMobile?: boolean;
  hasRating: boolean;
}

export const HotelRating = ({
  score,
  text,
  categoryList,
  count,
  isMobile = false,
  hasRating,
}: HotelRatingProps) => {
  const { t } = useTranslation();
  if (!hasRating) return null;
  return (
    <Container
      $isMobile={isMobile}
      data-testid={TEST_ID_HOTEL_RATING_CONTAINER}
    >
      <StyledTitleContainer>
        <HotelRatingSquare value={score} count={score} />
        <StyledHeader>
          <StyledTitle isMobile={isMobile}>{text}</StyledTitle>
          <StyledSubHeader>
            {t('hotel.hotelRating.title', { count })}
          </StyledSubHeader>
        </StyledHeader>
      </StyledTitleContainer>
      <CategoryListContainer isMobile={isMobile}>
        {categoryList.map((category) => (
          <StyledCategory key={category.categoryName} isMobile={isMobile}>
            <CategoryContainer>
              <div>{category.categoryName}</div>
              <div>{category.score}</div>
            </CategoryContainer>
            <StyledProgressBar>
              <StyledRatingProgressBar
                $progress={category.score * 10}
                $color={getRatingStyle(category.score)}
              />
            </StyledProgressBar>
          </StyledCategory>
        ))}
      </CategoryListContainer>
    </Container>
  );
};

const Container = styled.div<{ $isMobile: boolean }>`
  background: var(--white);
  border-radius: var(--border-radius);
  padding: ${({ $isMobile }) => ($isMobile ? 8 : 16)}px;
  height: fit-content;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const StyledTitle = styled.div<{ isMobile: boolean }>`
  color: var(--black);
  font-size: ${({ isMobile }) => (isMobile ? 18 : 22)}px;
  font-style: normal;
  font-weight: 500;
`;

const CategoryListContainer = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    `}
`;

const CategoryContainer = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: var(--black);

  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const StyledProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: var(--grey3);
  border-radius: 13px;
  overflow: hidden;
  position: relative;
`;

const StyledRatingProgressBar = styled.div<{
  $progress: number;
  $color: string;
}>`
  position: absolute;
  height: 4px;
  width: ${({ $progress }) => $progress}%;
  background: var(--black);
`;

const StyledCategory = styled.div<{ isMobile: boolean }>`
  margin-bottom: 16px;
  ${({ isMobile }) =>
    isMobile &&
    `
    display: flex;
    flex-direction: column;
    align-self: end;
    flex-basis: 45%;
  `}
`;

const StyledSubHeader = styled.div`
  color: #4d4d4d;
  font-size: 14px;
`;

const StyledHeader = styled.div`
  margin-left: 12px;
`;
